/**
 * Single source of truth for the app's primary navigation.
 *
 * Consumed by the Sidebar (rendering + permission gating) and the Header
 * (breadcrumb "group › item" for the current route). Keep every item's
 * `permissions` exactly as the backend RBAC expects — the Sidebar's visibility
 * predicate (`isNavItemVisible`) mirrors PermissionGate's structured-permissions
 * branch 1:1, so what you declare here is precisely who sees the link.
 *
 * Redesign phase 1 (docs/fulcrum-redesign.md): the former 13 collapsible
 * groups were regrouped into 6 always-open groups.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertTriangle,
  BarChart,
  BarChart3,
  Building2,
  Calendar,
  CalendarDays,
  CalendarRange,
  CircleCheck,
  ClipboardList,
  Clock,
  Cloud,
  Database,
  PoundSterling,
  FileText,
  Folder,
  FolderOpen,
  Gauge,
  Gift,
  GitBranch,
  Globe,
  GraduationCap,
  Key,
  SquareKanban,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Palette,
  PieChart,
  Receipt,
  Settings,
  Shield,
  ShieldCheck,
  Sun,
  Target,
  Thermometer,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
  Workflow,
  Wrench,
} from 'lucide-react';

export interface NavPermission {
  resource: string;
  action: string;
  scope?: string;
}

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  /** Optional count/label pill rendered on the right of the item. */
  badge?: string;
  /**
   * Structured permissions (same shape PermissionGate takes). Omitted/empty =
   * visible to every authenticated user (e.g. Surveys).
   */
  permissions?: NavPermission[];
  /** Mirrors PermissionGate's `requireAll` (default true). */
  requireAll?: boolean;
  /** Additional route prefixes that belong to this nav item (e.g. '/user' for user detail pages). */
  childRoutes?: string[];
}

export interface NavGroup {
  id: string;
  name: string;
  items: NavItem[];
  /**
   * Renders the expandable ClickUp workspace tree at the end of the group,
   * gated by `CLICKUP_TREE_PERMISSIONS` (it counts towards group visibility).
   */
  includesClickUpTree?: boolean;
}

/** Gate for the ClickUp workspace tree (unchanged from the pre-redesign sidebar). */
export const CLICKUP_TREE_PERMISSIONS: NavPermission[] = [{ resource: 'imports', action: 'read' }];

/**
 * Gate for the project LIST pages (Builds / Internal) — nav items AND routes
 * (App.tsx). Their list endpoints require `projects:view` at GLOBAL scope, so self-/client-
 * scoped roles (team_member, account_manager) must not be offered a page that only 403s.
 * Project DETAIL routes stay at any scope (backend checks the project itself).
 */
export const PROJECT_LIST_PERMISSIONS: NavPermission[] = [{ resource: 'projects', action: 'view', scope: 'global' }];

/** Retainers list reads `GET /api/retainers`, which is gated on `projects:browse` (global) instead. */
export const RETAINER_LIST_PERMISSIONS: NavPermission[] = [{ resource: 'projects', action: 'browse', scope: 'global' }];

/** Personal settings live in the header user menu (not the sidebar) since the redesign. */
export const SETTINGS_NAV_ITEM: NavItem = {
  name: 'Settings',
  href: '/settings',
  icon: Settings,
  permissions: [{ resource: 'settings', action: 'view', scope: 'self' }],
};

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    name: 'Overview',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard, permissions: [{ resource: 'dashboard', action: 'view' }] },
      { name: 'Pod dashboard', href: '/pod-dashboard', icon: TrendingUp, permissions: [{ resource: 'pods', action: 'view' }] },
      { name: 'Team calendar', href: '/team-calendar', icon: CalendarDays, permissions: [{ resource: 'absences', action: 'view' }] },
      { name: '2FA codes', href: '/2fa-codes', icon: Key, permissions: [{ resource: 'projects', action: 'view' }] },
    ],
  },
  {
    id: 'time-leave',
    name: 'Time & leave',
    items: [
      { name: 'Worktime', href: '/worktime', icon: Clock, permissions: [{ resource: 'worktime', action: 'view', scope: 'global' }] },
      { name: 'Time tracking', href: '/time-tracking', icon: Activity, permissions: [{ resource: 'time_entries', action: 'view', scope: 'self' }] },
      { name: 'Holidays', href: '/holidays', icon: Sun, permissions: [{ resource: 'holidays', action: 'view', scope: 'self' }] },
      { name: 'Holiday approvals', href: '/holidays/approvals', icon: CircleCheck, permissions: [{ resource: 'holidays', action: 'approve' }] },
      { name: 'Holiday allowances', href: '/holidays/admin/allowances', icon: Wallet, permissions: [{ resource: 'holiday_allowances', action: 'view', scope: 'global' }] },
      { name: 'Sickness records', href: '/sickness', icon: Thermometer, permissions: [{ resource: 'sickness', action: 'view', scope: 'self' }] },
    ],
  },
  {
    id: 'people',
    name: 'People',
    items: [
      { name: 'Users', href: '/users', icon: Users, permissions: [{ resource: 'users', action: 'view', scope: 'global' }], childRoutes: ['/user/'] },
      { name: 'Employees', href: '/employees', icon: UserCog, permissions: [{ resource: 'employees', action: 'view', scope: 'global' }] },
      { name: 'Pods', href: '/pods', icon: Users, permissions: [{ resource: 'pods', action: 'view' }] },
      { name: 'Pod assignments', href: '/pod-assignments', icon: Workflow, permissions: [{ resource: 'pod_assignments', action: 'view' }] },
      { name: 'Assignment dashboard', href: '/pod-assignment-dashboard', icon: Calendar, permissions: [{ resource: 'project_assignments', action: 'view' }] },
      { name: 'Assignment timeline', href: '/assignment-timeline', icon: CalendarRange, permissions: [{ resource: 'project_assignments', action: 'view' }] },
      { name: 'Assignment management', href: '/assignment-management', icon: ListChecks, permissions: [{ resource: 'project_assignments', action: 'manage' }] },
      { name: 'Absence overview', href: '/absence-overview', icon: PieChart, permissions: [{ resource: 'absences', action: 'view', scope: 'global' }] },
      // Intentionally permission-less: every authenticated user answers surveys.
      { name: 'Surveys', href: '/surveys', icon: ClipboardList },
      { name: 'Training', href: '/training', icon: GraduationCap, permissions: [{ resource: 'training', action: 'view', scope: 'self' }] },
      { name: 'Expenses', href: '/expenses', icon: Receipt, permissions: [{ resource: 'expenses', action: 'view', scope: 'self' }] },
      { name: 'Benefits', href: '/benefits', icon: Gift, permissions: [{ resource: 'benefits', action: 'view', scope: 'self' }] },
    ],
  },
  {
    id: 'projects',
    name: 'Projects',
    includesClickUpTree: true,
    items: [
      { name: 'Builds', href: '/builds', icon: FolderOpen, permissions: PROJECT_LIST_PERMISSIONS },
      { name: 'Retainers', href: '/retainers', icon: Folder, permissions: RETAINER_LIST_PERMISSIONS },
      { name: 'Internal', href: '/internal', icon: Building2, permissions: PROJECT_LIST_PERMISSIONS },
      { name: 'Forecasts', href: '/forecasts', icon: TrendingUp, permissions: [{ resource: 'forecasts', action: 'view' }] },
      { name: 'Analytics dashboard', href: '/analytics', icon: BarChart3, permissions: [{ resource: 'analytics', action: 'view' }] },
      { name: 'Retrospective', href: '/retrospective', icon: Target, permissions: [{ resource: 'analytics', action: 'view' }] },
      { name: 'Reporting overview', href: '/reporting/overview', icon: BarChart, permissions: [{ resource: 'analytics', action: 'view' }] },
      { name: 'Epic reporting', href: '/epic-reporting', icon: Target, permissions: [{ resource: 'analytics', action: 'read' }] },
      { name: 'Sprint reporting', href: '/sprint-reporting', icon: SquareKanban, permissions: [{ resource: 'analytics', action: 'read' }] },
      { name: 'Quote reports', href: '/reporting/quotes', icon: PoundSterling, permissions: [{ resource: 'reporting', action: 'read' }] },
      { name: 'Short stories', href: '/reporting/short-stories', icon: FileText, permissions: [{ resource: 'analytics', action: 'read' }] },
    ],
  },
  {
    id: 'monitoring',
    name: 'Monitoring',
    items: [
      { name: 'SSR monitoring', href: '/ssr-monitoring', icon: Globe, permissions: [{ resource: 'monitoring', action: 'view', scope: 'global' }] },
      { name: 'Incidents', href: '/incidents', icon: AlertTriangle, permissions: [{ resource: 'incidents', action: 'view', scope: 'global' }] },
    ],
  },
  {
    id: 'admin',
    name: 'Admin',
    items: [
      { name: 'System admin', href: '/admin/system', icon: Shield, permissions: [{ resource: 'system', action: 'configure' }] },
      { name: 'Role management', href: '/admin/roles', icon: ShieldCheck, permissions: [{ resource: 'roles', action: 'manage', scope: 'global' }] },
      { name: 'Permission usage', href: '/admin/permission-usage', icon: BarChart3, permissions: [{ resource: 'roles', action: 'manage', scope: 'global' }] },
      { name: 'Audit dashboard', href: '/admin/audit', icon: Shield, permissions: [{ resource: 'audit_logs', action: 'view', scope: 'global' }] },
      { name: 'Retention policies', href: '/admin/retention-policies', icon: Database, permissions: [{ resource: 'system', action: 'configure' }] },
      { name: 'Import monitor', href: '/admin/imports', icon: Database, permissions: [{ resource: 'imports', action: 'view', scope: 'global' }] },
      { name: 'Queue monitor', href: '/queue-monitor', icon: Activity, permissions: [{ resource: 'imports', action: 'execute', scope: 'global' }] },
      { name: 'Performance', href: '/performance', icon: Gauge, permissions: [{ resource: 'imports', action: 'execute', scope: 'global' }] },
      { name: 'BreatheHR', href: '/admin/breathehr', icon: Users, permissions: [{ resource: 'imports', action: 'view', scope: 'global' }] },
      { name: 'ClickUp management', href: '/admin/clickup-management', icon: Settings, permissions: [{ resource: 'integrations', action: 'view', scope: 'global' }] },
      { name: 'Infrastructure', href: '/admin/infrastructure', icon: Cloud, permissions: [{ resource: 'infrastructure', action: 'view', scope: 'global' }] },
      { name: 'Epic analysis', href: '/admin/epic-analysis', icon: BarChart3, permissions: [{ resource: 'analytics', action: 'read', scope: 'global' }] },
      { name: 'Quote management', href: '/admin/quote-management', icon: FileText, permissions: [{ resource: 'analytics', action: 'read', scope: 'global' }] },
      { name: 'Tools', href: '/admin/tools', icon: Wrench, permissions: [{ resource: 'system', action: 'configure' }] },
      { name: 'Styles check', href: '/tools/styles-check', icon: Palette, permissions: [{ resource: 'tools', action: 'execute', scope: 'global' }] },
      { name: 'Working patterns', href: '/admin/working-patterns', icon: Clock, permissions: [{ resource: 'employees', action: 'view', scope: 'global' }] },
      { name: 'Departments', href: '/admin/departments', icon: Building2, permissions: [{ resource: 'employees', action: 'view', scope: 'global' }] },
      { name: 'Divisions', href: '/admin/divisions', icon: GitBranch, permissions: [{ resource: 'employees', action: 'view', scope: 'global' }] },
      { name: 'Locations', href: '/admin/locations', icon: MapPin, permissions: [{ resource: 'employees', action: 'view', scope: 'global' }] },
    ],
  },
];

/** Subset of the RBAC context the visibility predicate needs. */
export interface NavPermissionChecker {
  isLoading: boolean;
  hasAllPermissions: (permissions: NavPermission[]) => boolean;
  hasAnyPermission: (permissions: NavPermission[]) => boolean;
}

/**
 * Is a nav item visible for the current user?
 *
 * Exact mirror of PermissionGate's structured-permissions branch, so the
 * sidebar can filter BEFORE rendering (needed to hide empty groups) without
 * drifting from the gate's semantics:
 *  - while permissions load → visible (PermissionGate's anti-flicker rule);
 *  - no permissions declared → visible to everyone. NOTE PermissionGate itself
 *    DEFAULT-DENIES such items, which is why the sidebar must never wrap a
 *    permission-less item in it (CLAUDE.md "Chat Surveys" pitfall);
 *  - otherwise `requireAll` (default true) → all-of, else any-of.
 */
export const isNavItemVisible = (item: Pick<NavItem, 'permissions' | 'requireAll'>, rbac: NavPermissionChecker): boolean => {
  if (rbac.isLoading) return true;
  if (!item.permissions || item.permissions.length === 0) return true;
  return item.requireAll === false
    ? rbac.hasAnyPermission(item.permissions)
    : rbac.hasAllPermissions(item.permissions);
};

/**
 * How specifically `item` matches `pathname` (0 = no match). Longer = more
 * specific, so `/holidays/approvals` beats `/holidays` and only ONE item is
 * ever highlighted (the old sidebar lit up both).
 */
const matchScore = (item: NavItem, pathname: string): number => {
  if (item.href === pathname) return Number.MAX_SAFE_INTEGER;
  let score = 0;
  if (item.href !== '/' && pathname.startsWith(item.href)) score = item.href.length;
  for (const prefix of item.childRoutes ?? []) {
    if (pathname.startsWith(prefix)) score = Math.max(score, prefix.length);
  }
  return score;
};

export interface NavLocation {
  group: NavGroup;
  item: NavItem;
}

/** The single nav item (and its group) that owns `pathname`, if any. */
export const findNavLocation = (pathname: string): NavLocation | null => {
  let best: NavLocation | null = null;
  let bestScore = 0;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const score = matchScore(item, pathname);
      if (score > bestScore) {
        best = { group, item };
        bestScore = score;
      }
    }
  }
  return best;
};

/**
 * Routes that have no nav item of their own but still deserve a meaningful
 * breadcrumb (detail pages, nav-less tools). Checked only when no nav item matches.
 */
const EXTRA_BREADCRUMBS: Array<{ prefix: string; section: string; page: string }> = [
  { prefix: '/settings', section: 'Account', page: 'Settings' },
  { prefix: '/projects', section: 'Projects', page: 'Project' },
  { prefix: '/clickup/', section: 'Projects', page: 'ClickUp tasks' },
  { prefix: '/pod-lead-dashboard', section: 'Overview', page: 'Pod lead dashboard' },
  { prefix: '/pod-utilization', section: 'People', page: 'Pod utilization' },
  { prefix: '/holidays/admin/bulk-operations', section: 'Time & leave', page: 'Holiday bulk operations' },
  { prefix: '/admin', section: 'Admin', page: 'Administration' },
];

const humanizeSegment = (segment: string): string => {
  const words = decodeURIComponent(segment).replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Fulcrum';
};

export interface Breadcrumb {
  section: string;
  page: string;
}

/** "Section › Page" for the header, derived from the nav config. */
export const getBreadcrumb = (pathname: string): Breadcrumb => {
  const location = findNavLocation(pathname);
  if (location) return { section: location.group.name, page: location.item.name };

  const extra = EXTRA_BREADCRUMBS.find(entry => pathname.startsWith(entry.prefix));
  if (extra) return { section: extra.section, page: extra.page };

  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? '';
  return { section: 'Fulcrum', page: humanizeSegment(firstSegment) };
};
